import { useSyncExternalStore } from 'react'

/**
 * Utility functions for managing notification sound settings
 */

const STORAGE_KEY = 'notification_sound_enabled'
/** Fired on this tab when the setting changes; the `storage` event covers other tabs. */
const CHANGE_EVENT = 'notification-sound-enabled-change'

/**
 * Check if notification sounds are enabled globally
 * @returns boolean indicating if notification sounds should be played
 */
export const isNotificationSoundEnabled = (): boolean => {
    // Check localStorage for the global notification sound setting
    // Default to true if not set (enabled by default)
    return localStorage.getItem(STORAGE_KEY) !== 'false'
}

/** Persist the global notification sound setting and notify every `useNotificationSoundEnabled` consumer. */
export const setNotificationSoundEnabled = (enabled: boolean): void => {
    localStorage.setItem(STORAGE_KEY, String(enabled))
    window.dispatchEvent(new Event(CHANGE_EVENT))
}

const subscribe = (onChange: () => void) => {
    const onStorage = (e: StorageEvent) => {
        if (e.key === STORAGE_KEY) onChange()
    }
    window.addEventListener(CHANGE_EVENT, onChange)
    window.addEventListener('storage', onStorage)
    return () => {
        window.removeEventListener(CHANGE_EVENT, onChange)
        window.removeEventListener('storage', onStorage)
    }
}

/** The global notification sound setting, kept in sync across every toggle and open tab. */
export const useNotificationSoundEnabled = (): boolean => useSyncExternalStore(subscribe, isNotificationSoundEnabled)

/**
 * Play the notification sound, logging instead of throwing when the browser refuses to play it.
 * Whether sound is on is enforced by `audioService.playSound()` itself.
 * @param audioService The audio service instance
 * @returns Promise<void>
 */
export const playNotificationSoundIfEnabled = async (audioService: { isInitialized(): boolean; playSound(): Promise<void> }): Promise<void> => {
    if (audioService.isInitialized()) {
        try {
            await audioService.playSound()
        } catch (err) {
            console.error('Error playing notification sound:', err)
        }
    }
}
